import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { TranslocoDatePipe } from '@jsverse/transloco-locale';
import { Store } from '@ngrx/store';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxLoadIndicatorModule } from 'devextreme-angular/ui/load-indicator';
import { DxSelectBoxModule } from 'devextreme-angular/ui/select-box';
import { DxTabsModule } from 'devextreme-angular/ui/tabs';
import { DxTextAreaModule } from 'devextreme-angular/ui/text-area';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { StatusBadgeComponent } from 'src/app/components/library/status-badge/status-badge.component';
import { FormPopupComponent } from 'src/app/components/utils/form-popup/form-popup.component';
import { NotifyService } from 'src/app/core/services/notify.service';
import {
    BLOCK_DESCRIPTION_MAX_LENGTH,
    BLOCK_REASON_TYPES,
    BlockableEntityType,
    BlockReasonType,
    ENTITY_BLOCK_STATUS_TRANSLATION_PREFIX,
    IEntityBlockRecord,
    IEntityBlockUpdateRequest,
} from 'src/app/features/entity-blocks/models/entity-block.models';
import { EntityBlockService } from 'src/app/features/entity-blocks/services/entity-block.service';
import { selectUsers } from 'src/app/features/user-management/store/selectors/user-management.selectors';
import { normalizeEntityId } from 'src/app/shared/helpers/entity-id.helper';
import { formatDateWithTransloco } from 'src/app/shared/helpers/format.helper';
import { ConfirmDialogService } from 'src/app/shared/services/confirm-dialog.service';
import { ButtonSection, IFooterButton } from 'src/app/types/footerButtons';

interface IEntityBlockEditFormData {
    reason: BlockReasonType | null;
    description: string;
}

@Component({
    selector: 'app-entity-block-management-dialog',
    standalone: true,
    imports: [
        CommonModule,
        TranslocoPipe,
        FormPopupComponent,
        StatusBadgeComponent,
        DxTabsModule,
        DxTemplateModule,
        DxSelectBoxModule,
        DxTextAreaModule,
        DxLoadIndicatorModule,
    ],
    providers: [TranslocoDatePipe],
    templateUrl: './entity-block-management-dialog.component.html',
    styleUrl: './entity-block-management-dialog.component.scss',
})
export class EntityBlockManagementDialogComponent {
    private readonly translocoService = inject(TranslocoService);
    private readonly translocoDatePipe = inject(TranslocoDatePipe);
    private readonly entityBlockService = inject(EntityBlockService);
    private readonly store = inject(Store);
    private readonly notifyService = inject(NotifyService);
    private readonly confirmDialogService = inject(ConfirmDialogService);

    readonly descriptionMaxLength = BLOCK_DESCRIPTION_MAX_LENGTH;
    readonly blockedStatus = 'Blocked';

    visible = input(false);
    entityType = input<BlockableEntityType>(BlockableEntityType.Customer);
    entityId = input<string | null>(null);

    visibleChange = output<boolean>();
    entityUpdated = output<unknown>();

    readonly blocks = signal<IEntityBlockRecord[]>([]);
    readonly selectedBlockIndex = signal(0);
    readonly isLoading = signal(false);
    readonly isSubmitting = signal(false);
    readonly isEditing = signal(false);
    readonly users = toSignal(this.store.select(selectUsers), { initialValue: [] });
    readonly editFormData = signal<IEntityBlockEditFormData>({
        reason: null,
        description: '',
    });

    readonly reasonOptions = computed(() =>
        BLOCK_REASON_TYPES.map(reason => ({
            value: reason,
            text: this.translocoService.translate(`enum_block_reason_${reason}`),
        }))
    );

    readonly entityTypeLabel = computed(() => this.translocoService.translate(`enum_blockable_entity_type_${this.entityType()}`));

    readonly dialogTitleText = computed(() =>
        this.translocoService.translate('title_entity_blocked', {
            entityType: this.entityTypeLabel(),
        })
    );

    readonly statusTranslationPrefix = computed(() => ENTITY_BLOCK_STATUS_TRANSLATION_PREFIX[this.entityType()]);

    readonly selectedBlock = computed(() => {
        const index = this.selectedBlockIndex();
        const items = this.blocks();

        if (index < 0 || index >= items.length) {
            return null;
        }

        return items[index];
    });

    readonly tabItems = computed(() =>
        this.blocks().map(block => ({
            id: block.id,
            text: this.translocoService.translate(`enum_block_reason_${block.reason}`),
        }))
    );

    readonly selectedTabItem = computed(() => this.tabItems()[this.selectedBlockIndex()] ?? null);

    readonly showTabs = computed(() => this.blocks().length > 1);

    readonly selectedReasonLabel = computed(() => {
        const reason = this.isEditing() ? this.editFormData().reason : this.selectedBlock()?.reason;

        if (!reason) {
            return '';
        }

        return this.translocoService.translate(`enum_block_reason_${reason}`);
    });

    readonly blockedSinceLabel = computed(() => {
        const createdAt = this.selectedBlock()?.createdAt;

        if (!createdAt) {
            return '';
        }

        return formatDateWithTransloco(this.translocoDatePipe, createdAt, false);
    });

    readonly blockedByLabel = computed(() => {
        const createdBy = normalizeEntityId(this.selectedBlock()?.createdBy);

        if (!createdBy) {
            return this.translocoService.translate('notSet');
        }

        const user = this.users().find(item => normalizeEntityId(item.id) === createdBy);
        return user?.displayName?.trim() || this.translocoService.translate('notSet');
    });

    readonly canSave = computed(() => !!this.editFormData().reason);

    readonly footerButtons = computed<IFooterButton[]>(() => {
        if (this.isEditing()) {
            return [
                {
                    text: 'cancel',
                    action: 'cancel-edit',
                    section: ButtonSection.Right,
                    type: 'normal',
                    stylingMode: 'outlined',
                    cssClass: 'dialog-btn-delivery-valid-close',
                    disabled: this.isSubmitting(),
                },
                {
                    text: 'save',
                    action: 'save',
                    section: ButtonSection.Right,
                    type: 'default',
                    stylingMode: 'contained',
                    cssClass: 'dialog-btn-delivery-valid-confirm',
                    disabled: !this.canSave() || this.isSubmitting(),
                },
            ];
        }

        return [
            {
                text: 'delete_block',
                action: 'delete',
                section: ButtonSection.Right,
                type: 'normal',
                stylingMode: 'outlined',
                cssClass: 'dialog-btn-delivery-valid-close',
                disabled: this.isSubmitting() || this.isLoading() || !this.selectedBlock(),
            },
            // {
            //     text: 'update_block',
            //     action: 'update',
            //     section: ButtonSection.Right,
            //     type: 'normal',
            //     stylingMode: 'outlined',
            //     cssClass: 'dialog-btn-delivery-valid-close',
            //     disabled: this.isSubmitting() || this.isLoading() || !this.selectedBlock(),
            // },
        ];
    });

    constructor() {
        effect(() => {
            if (!this.visible()) {
                return;
            }

            this.loadBlocks();
        });

        effect(() => {
            const block = this.selectedBlock();

            if (!block || this.isEditing()) {
                return;
            }

            this.resetEditForm(block);
        });
    }

    onVisibleChange(visible: boolean): void {
        this.visibleChange.emit(visible);

        if (!visible) {
            this.resetDialogState();
        }
    }

    onFooterClicked(action: string): void {
        switch (action) {
            case 'update':
                this.startEditing();
                break;
            case 'cancel-edit':
                this.cancelEditing();
                break;
            case 'save':
                this.saveBlock();
                break;
            case 'delete':
                this.confirmDeleteBlock();
                break;
        }
    }

    onTabClick(event: { itemIndex?: number }): void {
        if (this.isEditing() || event.itemIndex == null) {
            return;
        }

        this.selectedBlockIndex.set(event.itemIndex);
    }

    onReasonChanged(reason: BlockReasonType | null): void {
        this.editFormData.update(current => ({
            ...current,
            reason,
        }));
    }

    onDescriptionChanged(description: string): void {
        this.editFormData.update(current => ({
            ...current,
            description,
        }));
    }

    private loadBlocks(): void {
        const entityId = this.entityId();

        if (!entityId) {
            this.blocks.set([]);
            return;
        }

        this.isLoading.set(true);
        this.entityBlockService
            .getEntityBlocks(this.entityType(), entityId)
            .pipe(
                catchError(err => {
                    this.notifyService.notifyError(err);
                    this.blocks.set([]);
                    return EMPTY;
                }),
                tap(blocks => {
                    this.blocks.set(blocks);
                    this.selectedBlockIndex.set(0);
                    this.isEditing.set(false);
                }),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe();
    }

    private startEditing(): void {
        const block = this.selectedBlock();

        if (!block) {
            return;
        }

        this.resetEditForm(block);
        this.isEditing.set(true);
    }

    private cancelEditing(): void {
        const block = this.selectedBlock();

        if (block) {
            this.resetEditForm(block);
        }

        this.isEditing.set(false);
    }

    private saveBlock(): void {
        const block = this.selectedBlock();
        const entityId = this.entityId();
        const { reason, description } = this.editFormData();

        if (!block || !entityId || !reason || this.isSubmitting()) {
            return;
        }

        const request: IEntityBlockUpdateRequest = {
            id: block.id,
            entityType: this.entityType(),
            entityId,
            reason,
            description: description?.trim() || null,
        };

        this.isSubmitting.set(true);
        this.entityBlockService
            .updateEntityBlock(request)
            .pipe(
                catchError(err => {
                    this.notifyService.notifyError(err);
                    return EMPTY;
                }),
                tap(updatedEntity => {
                    this.isEditing.set(false);
                    this.notifyService.notifySuccess(this.translocoService.translate('block_updated_successfully'));
                    this.entityUpdated.emit(updatedEntity);
                    this.loadBlocks();
                }),
                finalize(() => this.isSubmitting.set(false))
            )
            .subscribe();
    }

    private confirmDeleteBlock(): void {
        const block = this.selectedBlock();

        if (!block || this.isSubmitting()) {
            return;
        }

        this.confirmDialogService
            .confirmCloseConfirm(
                this.translocoService.translate('confirm_delete_block_message'),
                this.translocoService.translate('confirm_delete_block_title'),
                { headerIconClass: 'cn-icon-ico-flag-warning' }
            )
            .then(confirmed => {
                if (confirmed) {
                    this.deleteBlock(block.id);
                }
            });
    }

    private deleteBlock(blockId: string): void {
        const entityId = this.entityId();

        if (!entityId || this.isSubmitting()) {
            return;
        }

        this.isSubmitting.set(true);
        this.entityBlockService
            .deleteEntityBlock(blockId, this.entityType(), entityId)
            .pipe(
                catchError(err => {
                    this.notifyService.notifyError(err);
                    return EMPTY;
                }),
                tap(updatedEntity => {
                    this.notifyService.notifySuccess(this.translocoService.translate('block_deleted_successfully'));
                    this.entityUpdated.emit(updatedEntity);
                    this.onVisibleChange(false);
                }),
                finalize(() => this.isSubmitting.set(false))
            )
            .subscribe();
    }

    private resetEditForm(block: IEntityBlockRecord): void {
        this.editFormData.set({
            reason: block.reason,
            description: block.description ?? '',
        });
    }

    private resetDialogState(): void {
        this.blocks.set([]);
        this.selectedBlockIndex.set(0);
        this.isEditing.set(false);
        this.isLoading.set(false);
        this.isSubmitting.set(false);
        this.editFormData.set({
            reason: null,
            description: '',
        });
    }
}
